import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { RIGHTS_OPEN_ENDED, isOpenEnded } from "@shared/content-rights";
import { emptyRightsDraft, type ContentRightsDraft } from "@shared/asset-draft";
import { tokens } from "../../theme";
import { CollectionRow, Field } from "./editorBits";

/**
 * Availability - which is to say, `contentRights` (`BE-7`, edited by `BE-15`).
 *
 * ## There is no publish switch here because there is none anywhere
 *
 * The catalogue has no `published`, `status`, `state`, `draft` or `active` field
 * on an asset. A row is live the instant it is POSTed. The one mechanism with
 * storage, CRUD and an enforcement path that actually runs is the licence
 * window, and exactly one upstream query applies it:
 *
 * ```ts
 * contentRights: filter?.availableInCountry
 *   ? { some: { marketCountry, availableFrom: {lte: now}, availableTo: {gte: now} } }
 *   : undefined
 * ```
 *
 * ⚠️ **No rights row means UNRESTRICTED** - available everywhere, forever -
 * because everything that ever imported did so without one. Adding the first
 * window therefore NARROWS a title rather than publishing it, and the empty
 * state below says so out loud. Getting that backwards would take a catalogue
 * that plays everywhere and make it play nowhere.
 *
 * ## What is deliberately not offered
 *
 * - **`visibleFrom`.** Inert upstream: written, stored, and read by nothing at
 *   all. Offering it would promise a two-stage visible-then-playable lifecycle
 *   that no consumer implements. The request boundary refuses a value that
 *   differs from `availableFrom` for the same reason.
 * - **A bare date as the end of a window.** "Available to 1 March" means the end
 *   of that day to whoever signed the licence and the start of it to
 *   `Date.parse` - a 24-hour error in the direction that serves content nobody
 *   is licensed to serve. The server refuses it; this editor sends timestamps.
 * - **Overlapping windows for one market.** They do not break playback (the
 *   filter is a `some()`), they break the operator, who now has two rows
 *   claiming different things with no way to tell which is in force.
 */

/** `<input type="datetime-local">` wants local wall-clock with no zone. */
function toLocalInput(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? value : new Date(ms).toISOString();
}

export default function AvailabilityEditor({
  rows,
  onChange,
}: {
  rows: ContentRightsDraft[];
  onChange: (next: ContentRightsDraft[]) => void;
}) {
  function update(index: number, patch: Partial<ContentRightsDraft>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  return (
    <Stack spacing={2}>
      {rows.length === 0 && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px dashed ${tokens.divider}`,
          }}
        >
          <Typography sx={{ fontSize: 13, color: tokens.ink, mb: 0.5 }}>
            No licence window — available everywhere, forever.
          </Typography>
          <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
            That is the catalogue&apos;s import default rather than a decision
            anybody made. Adding a window NARROWS this row to the markets you
            list; nothing outside them will be licensed.
          </Typography>
        </Box>
      )}

      {rows.map((row, index) => {
        const openEnded = isOpenEnded(row.availableTo);
        return (
          <CollectionRow
            key={row.contentRightsId ?? `new-${index}`}
            title={
              row.marketCountry
                ? `${row.marketCountry.toUpperCase()}${
                    row.region ? ` · ${row.region}` : ""
                  }`
                : "New window"
            }
            removeLabel="Remove window"
            onRemove={() => onChange(rows.filter((_, i) => i !== index))}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <Field
                label="Market country"
                placeholder="se"
                value={row.marketCountry}
                onChange={(e) =>
                  update(index, { marketCountry: e.target.value })
                }
                helperText="Two or three lowercase letters."
                inputProps={{ "data-testid": `rights-market-${index}` }}
              />
              <Field
                label="Region"
                placeholder="Leave blank for the whole market"
                value={row.region}
                onChange={(e) => update(index, { region: e.target.value })}
                helperText="Nothing upstream filters on this."
              />
              <Field
                label="Max concurrent streams"
                value={row.maxConcurrentStreams}
                onChange={(e) =>
                  update(index, { maxConcurrentStreams: e.target.value })
                }
                helperText="0 = not enforced. Nothing reads it."
              />

              <Field
                type="datetime-local"
                label="Available from"
                value={toLocalInput(row.availableFrom)}
                onChange={(e) =>
                  update(index, {
                    availableFrom: fromLocalInput(e.target.value),
                  })
                }
              />
              <Field
                type="datetime-local"
                label="Available to"
                value={openEnded ? "" : toLocalInput(row.availableTo)}
                disabled={openEnded}
                onChange={(e) =>
                  update(index, { availableTo: fromLocalInput(e.target.value) })
                }
              />
              <Stack justifyContent="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={openEnded}
                      onChange={(e) =>
                        update(index, {
                          availableTo: e.target.checked
                            ? RIGHTS_OPEN_ENDED
                            : new Date(
                                Date.now() + 365 * 24 * 3600 * 1000
                              ).toISOString(),
                        })
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12 }}>No end date</Typography>
                  }
                />
              </Stack>
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={row.encrypted}
                    onChange={(e) =>
                      update(index, { encrypted: e.target.checked })
                    }
                  />
                }
                label={
                  <Typography sx={{ fontSize: 12 }}>
                    Encrypted (licence-level flag)
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={row.allowedToDownload}
                    onChange={(e) =>
                      update(index, { allowedToDownload: e.target.checked })
                    }
                  />
                }
                label={
                  <Typography sx={{ fontSize: 12 }}>
                    Downloads allowed
                  </Typography>
                }
              />
            </Stack>
          </CollectionRow>
        );
      })}

      <Button
        size="small"
        variant="outlined"
        sx={{ alignSelf: "flex-start" }}
        data-testid="add-rights"
        onClick={() => onChange([...rows, emptyRightsDraft()])}
      >
        Add licence window +
      </Button>
    </Stack>
  );
}
